import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getDefaultRouteForRole, ROLE_ADMIN } from '../lib/roleContext';

const STORAGE_KEY = 'tla_view_as_role';
const ALLOWED_VIEW_AS_ROLES = new Set(['OWNER', 'USER']);

const ViewAsContext = createContext(null);

export function ViewAsProvider({ children }) {
  const { isAdmin, loading, bumpSessionRevision } = useAuth();
  const navigate = useNavigate();
  const [viewAsRole, setViewAsRoleState] = useState(() => {
    const storedRole = sessionStorage.getItem(STORAGE_KEY);
    return ALLOWED_VIEW_AS_ROLES.has(storedRole) ? storedRole : null;
  });

  // Clear view-as state only after auth has resolved and the real user is not admin.
  // While auth is loading, isAdmin is temporarily false, so clearing here would erase
  // sessionStorage on every refresh before the ADMIN account is restored.
  useEffect(() => {
    if (loading) return;

    if (!isAdmin) {
      sessionStorage.removeItem(STORAGE_KEY);
      setViewAsRoleState(null);
    }
  }, [isAdmin, loading]);

  const applyViewAsRole = (role) => {
    if (!isAdmin) return;

    if (role && !ALLOWED_VIEW_AS_ROLES.has(role)) return;

    if (role) {
      sessionStorage.setItem(STORAGE_KEY, role);
      console.info(`[Admin QA] Entering view-as mode: ${role}`);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
      console.info('[Admin QA] Returned to Admin mode');
    }
    setViewAsRoleState(role || null);
    bumpSessionRevision();
    navigate(getDefaultRouteForRole(role || ROLE_ADMIN), { replace: true });
  };

  const setViewAsRole = (role) => applyViewAsRole(role);

  const clearViewAs = () => applyViewAsRole(null);

  return (
    <ViewAsContext.Provider value={{ viewAsRole, setViewAsRole, clearViewAs }}>
      {children}
    </ViewAsContext.Provider>
  );
}

export function useViewAs() {
  const context = useContext(ViewAsContext);
  if (!context) throw new Error('useViewAs must be used within ViewAsProvider');
  return context;
}

export function clearViewAsStorage() {
  sessionStorage.removeItem(STORAGE_KEY);
}
